/**
 * Delivery / free-gift progress bar (Figma 44940:5066).
 *
 * One bar, two placements — both rendered by snippets/threshold-bar.liquid:
 *   • PDP  → main-product.liquid `threshold_bar` block (above the buy buttons)
 *   • cart → cart-drawer.liquid   (above the GWP picker)
 * Skinned by assets/threshold-bar.css. Sits directly above the Kite free-gift
 * picker (`.noted-gwp`) in both places — the picker IS the "select your free
 * gift" CTA from the design; this file only draws the pills + progress + message.
 *
 * Tiers come from TWO sources, merged + sorted by amount (source-agnostic):
 *   1. Custom tiers — typed by the merchant in theme settings (free delivery, or
 *      gifts from other apps). Server-rendered into the config JSON, so they're
 *      known with zero JS and never flash.
 *   2. App gift tiers — pulled at runtime from the Kite free-gift app
 *      (`window.gfg.settings.freeGifts[].rulesList[].minimumCartValue`). This is
 *      the ONLY client-only data, so it's the only thing that could flash → we
 *      cache it in localStorage and replay it synchronously on the next load, then
 *      reconcile against the live `window.gfg` once it's ready.
 *
 * Live updates: the cart total drives everything. On `cart:change` we refresh it
 * from /cart.js and re-render every bar. In the drawer, cart.js also re-renders
 * the bar's server HTML on each swap (fresh total) — we re-hydrate the Kite tiers
 * on top via the same event + a DOM observer.
 *
 * Spend and save (3 Nov 2026, plan: .assistant/work/spend-and-save/plan.html).
 * As soon as a custom tier is a "discount" (percent off), or the bar counts
 * bottles (threshold_mode = quantity, set per market), the bar draws a LADDER
 * instead (renderLadder): two-line pills ("Save 20%" over "£20"), a marker
 * where each step is reached, and a saving line once one is.
 *   • Discount tiers are measured on the FULL-PRICE basket (original line
 *     prices, gift lines left out), so the bar never goes backwards when the
 *     discount comes off.
 *   • Free delivery is judged by checkout on the total AFTER discount (checked
 *     3 Nov), so with "Free delivery counts: after" the delivery pill sits at
 *     the lowest full-price basket that still clears the delivery amount once
 *     the discount it reaches comes off (£35 with 20/30/40% tiers = £50).
 *     Worked out from the configured tiers, so it doesn't matter whether the
 *     discount is already showing in the basket.
 *   • Bottles mode counts every line's quantity except Kite gift lines, which
 *     is what Kite's SAVEMORE counts. SAVEMORE only discounts a set of 2 or 3,
 *     so the saving line names the bottles ("20% off 2 bottles"), no figure.
 * With no discount tier and money mode, everything below runs as before.
 */
(function () {
  'use strict';
  if (window.__notedThresholdBar) return;
  window.__notedThresholdBar = true;

  var CACHE_KEY = 'noted:gwpTiers';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ───────────────────────── money ───────────────────────── */
  // Format for the customer's MARKET, not the published locale (which is only
  // 'en' today), so a euro threshold reads "50,00 €" not the en-GB "€50.00".
  // See assets/main-product.js marketLocale for the full rationale.
  function marketLocale(currency, cfgLocale) {
    if (currency !== 'EUR') return cfgLocale || 'en-GB';
    var country = (window.Shopify && Shopify.country) || 'GB';
    return { NL: 'nl-NL', IE: 'en-IE', MT: 'en-IE', CY: 'en-IE' }[country] || 'de-DE';
  }
  var fmt = null;
  function money(pence, cfg) {
    var major = (pence || 0) / 100;
    var currency = cfg.currency || 'GBP';
    try {
      if (!fmt) {
        fmt = new Intl.NumberFormat(marketLocale(currency, cfg.locale), {
          style: 'currency', currency: currency
        });
      }
      return fmt.format(major);
    } catch (e) {
      return (currency === 'GBP' ? '£' : '') + major.toFixed(2);
    }
  }
  // Whole amounts without the pence ("£20", "20 €"), for the ladder pills.
  var fmtShort = null;
  function moneyShort(pence, cfg) {
    if ((pence || 0) % 100 !== 0) return money(pence, cfg);
    var currency = cfg.currency || 'GBP';
    try {
      if (!fmtShort) {
        fmtShort = new Intl.NumberFormat(marketLocale(currency, cfg.locale), {
          style: 'currency', currency: currency, minimumFractionDigits: 0, maximumFractionDigits: 0
        });
      }
      return fmtShort.format((pence || 0) / 100);
    } catch (e) {
      return (currency === 'GBP' ? '£' : '') + ((pence || 0) / 100);
    }
  }

  /* ───────────────────────── icons ───────────────────────── */
  // Inline SVGs (the Liquid `icon` snippet can't be called from JS). 24×24,
  // stroke=currentColor — same glyphs as snippets/icon-*.liquid.
  var ICONS = {
    gift: '<path d="M4 11h16v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-9ZM3 7h18v4H3zM12 21V7M12 7S10.5 3 7.75 3 5 6 5 6s2.5 1 7 1ZM12 7s1.5-4 4.25-4S19 6 19 6s-2.5 1-7 1Z"/>',
    truck: '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>',
    'check-circle': '<path d="M9 12.5 11 14.5 15.5 10"/><circle cx="12" cy="12" r="9"/>',
    percent: '<path d="M19 5 5 19"/><circle cx="6.75" cy="6.75" r="2.25"/><circle cx="17.25" cy="17.25" r="2.25"/>',
    sparkles: '<path d="M11 3.5 13 8.5 18 10.5 13 12.5 11 17.5 9 12.5 4 10.5 9 8.5 11 3.5ZM18.5 14 19.5 16.75 22.25 17.75 19.5 18.75 18.5 21.5 17.5 18.75 14.75 17.75 17.5 16.75 18.5 14Z"/>',
    fire: '<path d="M12 2.5s4.5 3.8 4.5 8.3a4.5 4.5 0 0 1-9 0c0-1.6.8-2.8.8-2.8S5 10 5 14a7 7 0 0 0 14 0c0-6.4-7-11.5-7-11.5Z"/>',
    heart: '<path d="M19.5 12.572 12 20l-7.5-7.428A5 5 0 1 1 12 6.006a5 5 0 1 1 7.5 6.566Z"/>',
    leaf: '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/><path d="M2 21c0-3 1.85-5.36 5.08-6"/>',
    star: '<path d="M12 3l2.575 5.217 5.758.837-4.166 4.061.983 5.733L12 16.896 6.85 19.85l.983-5.733L3.667 9.054l5.758-.837L12 3Z"/>'
  };
  function iconSvg(name) {
    var path = ICONS[name] || ICONS.gift;
    return '<svg class="noted-threshold-bar__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + path + '</svg>';
  }

  /* ───────────────────────── Kite tiers ───────────────────── */
  // Kite stores `minimumCartValue` as a string in the MAJOR currency unit
  // (verified live: "37.99" = £37.99); the cart total is in minor units (pence),
  // so scale ×100 to compare. (GBP store — 2dp currencies only.)
  function normalizeAmount(v) {
    var n = parseFloat(v);
    if (!isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }

  // Read gift thresholds straight from Kite's runtime settings. Returns
  // [{ amount(pence), kind:'gift' }] or null when window.gfg isn't ready yet.
  //
  // Kite ships settings on EVERY market, including ones the merchant excluded
  // from the campaign — it just declines to render there. So each campaign is
  // filtered through the market gate (assets/gwp-market.js); an excluded market
  // contributes no gift tier, which is what stops the bar promising a gift that
  // can't be claimed. Delivery tiers are merchant config and are unaffected.
  function readKiteTiers() {
    var g = window.gfg;
    var st = g && g.settings;
    var gifts = st && st.freeGifts;
    var giftsV2 = st && st.freeGiftsV2;
    if (!Array.isArray(gifts) && !Array.isArray(giftsV2)) return null;
    var gate = window.NotedGwpMarket;
    var seen = {}, out = [];
    function push(tier) {
      if (tier.amount > 0 && !seen[tier.amount]) { seen[tier.amount] = 1; out.push(tier); }
    }

    // Kite V1 — freeGifts[].rulesList[].minimumCartValue (spend thresholds).
    (gifts || []).forEach(function (fg, i) {
      if (gate && !gate.allows(fg, i)) return;
      var rules = (fg && fg.rulesList) || [];
      rules.forEach(function (r) {
        push({ amount: normalizeAmount(r && r.minimumCartValue), kind: 'gift' });
      });
    });

    // Kite V2 — freeGiftsV2[].segmentsData.segmentsList[].buyXRulesData.rulesList[].
    // Kite moved Noted onto V2 on 28 Sep 2026; both shapes are read so the bar
    // follows whichever one the merchant runs. Two trigger kinds are drawn:
    //   cartSubtotal  → a spend threshold, same as V1
    //   cartQuantity  → "buy any item" (qty 1). No £ figure exists, so it is a
    //                   1p tier flagged anyItem: any paid line crosses it, and
    //                   the copy says "Add any item" rather than "Spend £0.01".
    // Anything else (qty > 1, product-specific rules) has no honest place on a
    // spend bar, so it is left to Kite's own widget.
    var V = g && g.gfgFreeGiftV2;
    (giftsV2 || []).forEach(function (c) {
      if (gate && gate.allowsV2 ? !gate.allowsV2(c) : (c && c.isEnabled === false)) return;
      var segs = (c && c.segmentsData && c.segmentsData.segmentsList) || [];
      segs.forEach(function (seg) {
        var rules = (seg && seg.buyXRulesData && seg.buyXRulesData.rulesList) || [];
        rules.forEach(function (r) {
          var rv = (r && r.ruleValue) || {};
          if (r.ruleType === 'cartSubtotal') {
            var major = parseFloat(rv.qualifierValue);
            try { if (V && V._resolveMarketQualifierValue) major = V._resolveMarketQualifierValue(rv, true); } catch (e) {}
            push({ amount: normalizeAmount(major), kind: 'gift' });
          } else if (r.ruleType === 'cartQuantity' && (parseFloat(rv.qualifierValue) || 0) <= 1) {
            push({ amount: 1, kind: 'gift', anyItem: true });
          }
        });
      });
    });
    return out;
  }

  // Cache is keyed PER MARKET. Without that, a UK visitor's cached gift tier
  // replays on a first paint in Ireland (where the gift isn't offered) before
  // the live read corrects it — a flash of a promise we can't keep.
  function cacheName() {
    var gate = window.NotedGwpMarket;
    return CACHE_KEY + ':' + (gate ? gate.key() : 'unknown');
  }
  function cacheKite(tiers) {
    try { localStorage.setItem(cacheName(), JSON.stringify(tiers)); } catch (e) {}
  }
  function cachedKite() {
    try {
      var raw = localStorage.getItem(cacheName());
      var arr = raw ? JSON.parse(raw) : null;
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }

  /* ───────────────────────── tier model ───────────────────── */
  // Merge custom (config) + gift (Kite/cache), sort by amount, resolve each
  // tier's reward + success label so the renderer stays dumb.
  function buildTiers(cfg, kiteTiers) {
    var custom = (cfg.customTiers || []).map(function (t) {
      return { amount: t.amount, icon: t.icon || 'gift', kind: 'custom', type: t.kind || 'delivery', reward: t.goal, success: t.success };
    });
    var gifts = (kiteTiers || []).map(function (t) {
      return { amount: t.amount, icon: 'gift', kind: 'gift', anyItem: !!t.anyItem };
    });
    var all = custom.concat(gifts).filter(function (t) { return t.amount > 0; });
    all.sort(function (a, b) { return a.amount - b.amount; });

    var giftCount = gifts.length, giftSeen = 0;
    all.forEach(function (t) {
      if (t.kind === 'gift') {
        giftSeen++;
        t.giftIndex = giftSeen;
        // Subtitle reward: first gift = "a free gift"; later gifts = "free gift 2/2".
        t.reward = (giftCount > 1 && giftSeen > 1) ? str('gift_reward_numbered').replace('{number}', giftSeen).replace('{count}', giftCount) : cfg.giftReward;
        t.success = giftSeen === 1 ? cfg.giftSuccess : cfg.giftSuccessMore;
        t.giftNumber = giftSeen;
        t.giftCount = giftCount;
      }
    });
    return { tiers: all, giftCount: giftCount };
  }

  // `valueOf(tier)` = the basket total that tier is judged on. That is the
  // cart total for every tier, except a delivery tier when "Free delivery
  // counts" is set to before discounts (then the full-price total).
  function computeState(model, valueOf, cfg, claimed) {
    var tiers = model.tiers;
    var completed = 0, next = null, nextIndex = -1, giftsCrossed = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (valueOf(tiers[i]) >= tiers[i].amount) { completed++; if (tiers[i].kind === 'gift') giftsCrossed++; }
      else if (!next) { next = tiers[i]; nextIndex = i; }
    }
    var cartTotal = next ? valueOf(next) : 0;

    // Segmented fill across equal-width pills: full columns for completed tiers
    // + a partial column for progress through the current segment.
    var fillFraction;
    if (!tiers.length) {
      fillFraction = 0;
    } else if (!next) {
      fillFraction = 1;
    } else {
      var prevAmount = nextIndex === 0 ? 0 : tiers[nextIndex - 1].amount;
      var span = next.amount - prevAmount;
      var seg = span > 0 ? (cartTotal - prevAmount) / span : 0;
      seg = Math.max(0, Math.min(1, seg));
      fillFraction = (completed + seg) / tiers.length;
    }

    // Headline + subtext.
    // `celebrating` = the headline is reporting something BANKED, not promising
    // something ahead. It drives the tick, which must never sit beside a forward
    // promise: with a crossed tier whose success line is blank, the headline
    // falls through to the next tier's goal, and gating the tick on the
    // completed count alone put a green ✓ next to "Get exclusive rewards".
    var headline, subtext, amountStr = null, celebrating = false;
    if (!next) {
      celebrating = true;
      headline = cfg.doneHeadline;
      subtext = cfg.doneSubtext;
    } else {
      if (next.anyItem) {
        // "Buy any item" gift (Kite V2 cartQuantity): there is no £ gap to quote.
        subtext = cfg.giftAnyItem
          .replace('{reward}', next.reward || str('your_reward'));
      } else {
        var remaining = money(next.amount - cartTotal, cfg);
        amountStr = remaining;                     // bolded on its own in the message (Figma)
        subtext = cfg.spendMore
          .replace('{amount}', remaining).replace('{reward}', next.reward || str('your_reward'));
      }
      if (completed === 0) {
        headline = headlineFor(next, model, cfg);
      } else {
        // Most-recently-crossed tier's success line.
        celebrating = !!tiers[completed - 1].success;
        headline = celebrating ? tiers[completed - 1].success : headlineFor(next, model, cfg);
      }
    }

    // A gift the spend has unlocked but that isn't in the basket yet. "You have
    // a free gift!" read as if it had been added, so until it is, the message
    // tells the shopper to go and pick it (Johanna, 25 Sep). This outranks the
    // spend-more line: choosing the gift is the action that matters right now,
    // and the delivery nudge comes back as soon as it's added.
    var choosing = false;
    if (giftsCrossed > (claimed || 0) && cfg.giftUnlocked) {
      choosing = true;
      celebrating = true;
      headline = cfg.giftUnlocked;
      subtext = cfg.giftChoose || '';
      amountStr = null;
    }

    return { tiers: tiers, completed: completed, fillFraction: fillFraction, headline: headline, subtext: subtext, amount: amountStr, celebrating: celebrating, choosing: choosing };
  }

  // The headline names the tier being worked towards RIGHT NOW, so it always
  // agrees with the subtext underneath it.
  //
  // It used to key off whether a gift existed ANYWHERE in the ladder, which is
  // a different question: with free delivery at £45 and the gift above it, a
  // £22.99 basket read "Get your free gift" over "Spend another £22.00 to get
  // free delivery" — promising a reward two tiers away while asking for the
  // spend of one. `startHeadline` is the merchant's gift-flavoured copy, so it
  // only belongs on a gift tier; every other tier speaks with its own goal.
  function headlineFor(next, model, cfg) {
    if (next.kind === 'gift') {
      var h = cfg.startHeadline;
      if (model.giftCount > 1) h += ' ' + next.giftNumber + '/' + model.giftCount;
      return h;
    }
    // Never falls back to `startHeadline` here. That's the gift copy, and a
    // custom tier with its "Reward label" left blank is precisely a tier we know
    // nothing about — reaching for the gift wording put "Get your free gift"
    // back over "Spend another £24.99 to get your reward", the same
    // contradiction by a different route. 'your reward' is what the subtext
    // already says in that case, so the two lines agree by construction.
    return str('get_reward').replace('{reward}', next.reward || str('your_reward'));
  }

  /* ───────────────────────── render ───────────────────────── */
  function render(el, cart, kiteTiers, claimed) {
    var cfg = el.__cfg || (el.__cfg = readConfig(el));
    if (!cfg) return;
    if (isLadder(cfg)) { renderLadder(el, cfg, cart, kiteTiers, claimed); return; }
    var model = buildTiers(cfg, cfg.pullGwp ? kiteTiers : []);
    if (!model.tiers.length) { el.setAttribute('data-threshold-empty', ''); el.innerHTML = ''; return; }
    el.removeAttribute('data-threshold-empty');

    var before = cfg.deliveryBasis === 'before';
    var valueOf = function (t) {
      return (before && t.kind === 'custom' && t.type === 'delivery') ? cart.full : cart.total;
    };
    var state = computeState(model, valueOf, cfg, claimed);
    var n = state.tiers.length;

    if (cfg.layout === 'steps') { renderSteps(el, plainView(state, valueOf, cfg)); return; }

    if (n === 1 && el.hasAttribute('data-threshold-single-compact')) {
      renderSingle(el, state, valueOf(state.tiers[0]) >= state.tiers[0].amount);
      return;
    }

    var pills = state.tiers.map(function (t) {
      var done = valueOf(t) >= t.amount;
      var icon = done ? 'check-circle' : t.icon;
      var label = labelFor(t);
      return '<div class="noted-threshold-bar__pill' + (done ? ' is-complete' : '') + '">' +
        iconSvg(icon) + '<span class="noted-threshold-bar__label">' + esc(label) + '</span></div>';
    }).join('');

    var fillPct = (state.fillFraction * 100).toFixed(2);

    // Subtext bolds only the £amount (Figma: "Spend another £17.99 to get a free
    // gift" — amount bold, rest regular). split/join so no regex-escaping needed.
    var subHtml = '';
    if (state.subtext) {
      subHtml = esc(state.subtext);
      if (state.amount) {
        var amt = esc(state.amount);
        subHtml = subHtml.split(amt).join('<strong>' + amt + '</strong>');
      }
    }

    // Banking a tier is the moment worth celebrating, and it's the one the
    // pills alone under-sell (a filled pill reads as decoration). A tick beside
    // the headline is the 1.1 "All rewards unlocked" cue, kept for every
    // intermediate tier rather than only the last one — but only when the
    // headline is actually reporting the win (see `celebrating`).
    var tick = state.celebrating
      ? '<span class="noted-threshold-bar__tick">' + iconSvg('check-circle') + '</span>'
      : '';

    var html =
      '<div class="noted-threshold-bar__track">' +
        '<div class="noted-threshold-bar__pills" style="--pill-count:' + n + '">' + pills + '</div>' +
        '<div class="noted-threshold-bar__progress"><div class="noted-threshold-bar__fill" style="width:' + fillPct + '%"></div></div>' +
      '</div>' +
      '<p class="noted-threshold-bar__message">' + tick + '<strong>' + esc(state.headline) + '</strong>' +
        (subHtml ? '<br>' + subHtml : '') + '</p>';

    el.innerHTML = html;
    el.setAttribute('data-threshold-ready', '');
  }

  // One reward only (e.g. free delivery with no gift campaign live), in the
  // basket. Agreed on the 21 Oct call (Tim, Johanna, Kirill): with one reward the
  // pill row just repeats the headline and "You've unlocked every reward" is
  // wrong, so drop the pill + icon, put the headline ABOVE the bar, the
  // "Spend another…" line under it, and on success show the tier's own unlocked
  // line in the headline spot with nothing under the bar. Less height on mobile.
  // Opt-in per placement (data-threshold-single-compact, set by the cart drawer);
  // two or more rewards keep the pill row exactly as before.
  function renderSingle(el, state, done) {
    var tier = state.tiers[0];
    var headline = state.headline;
    var subtext = state.subtext;
    var amount = state.amount;
    var celebrating = state.celebrating;
    // Reached: speak about THIS reward, not "every reward". A gift that is
    // unlocked but not yet chosen keeps its "choose your free gift" prompt.
    if (done && !state.choosing) {
      headline = tier.success || headline;
      subtext = '';
      celebrating = true;
    }

    var subHtml = '';
    if (subtext) {
      subHtml = esc(subtext);
      if (amount) {
        var amt = esc(amount);
        subHtml = subHtml.split(amt).join('<strong>' + amt + '</strong>');
      }
    }
    var tick = celebrating
      ? '<span class="noted-threshold-bar__tick">' + iconSvg('check-circle') + '</span>'
      : '';
    var fillPct = (state.fillFraction * 100).toFixed(2);

    el.innerHTML =
      '<div class="noted-threshold-bar__single">' +
        '<p class="noted-threshold-bar__message noted-threshold-bar__message--above">' + tick + '<strong>' + esc(headline) + '</strong></p>' +
        '<div class="noted-threshold-bar__progress"><div class="noted-threshold-bar__fill" style="width:' + fillPct + '%"></div></div>' +
        (subHtml ? '<p class="noted-threshold-bar__message noted-threshold-bar__message--below">' + subHtml + '</p>' : '') +
      '</div>';
    el.setAttribute('data-threshold-ready', '');
  }

  /* ───────────────────────── spend and save ladder ────────── */
  function discountTiers(cfg) {
    return (cfg.customTiers || []).filter(function (t) {
      return t.kind === 'discount' && t.percent > 0 && t.percent < 100;
    }).map(function (t) {
      return { type: 'discount', pos: t.amount, amount: t.amount, pct: t.percent, success: t.success };
    }).sort(function (a, b) { return a.pos - b.pos; });
  }

  function isLadder(cfg) {
    return cfg.mode === 'quantity' || discountTiers(cfg).length > 0;
  }

  // Highest percent reached at `v` (the full-price total, or the bottle count).
  function pctAt(disc, v) {
    var p = 0;
    disc.forEach(function (d) { if (v >= d.pos && d.pct > p) p = d.pct; });
    return p;
  }

  // The saving at `pct`, line by line, rounded per line. That is how checkout
  // rounded it on 3 Nov (30% of £24.74 + £24.74 + £3.74 = £15.96, where 30% of
  // the £53.22 total would be £15.97).
  function savingAt(cart, pct) {
    if (!pct) return 0;
    var lines = cart.lines && cart.lines.length ? cart.lines : [cart.full];
    return lines.reduce(function (s, l) { return s + Math.round(l * pct / 100); }, 0);
  }

  // The lowest full-price basket at or above `from` whose total after the
  // discount it reaches still clears `need`. Each stretch between discount
  // tiers has one percent, so the answer is the first stretch where
  // need / (1 - percent) falls inside it. null = never.
  function nextReach(need, from, disc) {
    var bounds = [0].concat(disc.map(function (d) { return d.pos; }));
    for (var k = 0; k < bounds.length; k++) {
      var lo = bounds[k];
      var hi = k + 1 < bounds.length ? bounds[k + 1] : Infinity;
      if (hi <= from) continue;
      var p = pctAt(disc, lo);
      var c = Math.max(lo, from, Math.ceil(need * 100 / (100 - p) - 1e-9));
      if (c < hi) return c;
    }
    return null;
  }

  function fmtStr(key, vars) {
    var s = str(key);
    for (var k in vars) s = s.split('{' + k + '}').join(vars[k]);
    return s;
  }

  function buildLadder(cfg, kiteTiers, cart) {
    var qty = cfg.mode === 'quantity';
    var disc = discountTiers(cfg);
    var tiers = disc.slice();

    if (qty) {
      // EU-1: the first bottle is a step of its own, so the shopper sees one
      // marker reached before the first saving (Kirill, 29 Oct).
      if (disc.length && disc[0].pos > 1) tiers.push({ type: 'start', pos: 1, amount: 1 });
    } else {
      (cfg.customTiers || []).forEach(function (t) {
        if (t.kind === 'discount') return;
        if (t.kind === 'gift') {
          tiers.push({ type: 'gift', pos: t.amount, amount: t.amount, reward: t.goal, success: t.success, label: t.goal });
          return;
        }
        var pos = cfg.deliveryBasis === 'before' ? t.amount : nextReach(t.amount, 0, disc);
        if (pos == null) return;
        tiers.push({ type: 'delivery', pos: pos, amount: t.amount, reward: t.goal, success: t.success });
      });
      // App gift tiers keep today's measure (the basket total).
      if (cfg.pullGwp) {
        (kiteTiers || []).forEach(function (t) {
          tiers.push({ type: 'gift', kite: true, pos: t.amount, amount: t.amount, anyItem: !!t.anyItem });
        });
      }
    }

    var order = { start: 0, discount: 1, delivery: 2, gift: 3 };
    tiers = tiers.filter(function (t) { return t.pos > 0; });
    tiers.sort(function (a, b) { return (a.pos - b.pos) || (order[a.type] - order[b.type]); });

    var kites = tiers.filter(function (t) { return t.kite; });
    kites.forEach(function (t, i) {
      t.giftNumber = i + 1;
      t.giftCount = kites.length;
      t.reward = (kites.length > 1 && i > 0) ? str('gift_reward_numbered').replace('{number}', i + 1).replace('{count}', kites.length) : cfg.giftReward;
      t.success = i === 0 ? cfg.giftSuccess : cfg.giftSuccessMore;
    });

    var axis = qty ? cart.count : cart.full;
    tiers.forEach(function (t) {
      if (t.type === 'delivery') {
        t.reached = cfg.deliveryBasis === 'before'
          ? cart.full >= t.amount
          : cart.full - savingAt(cart, pctAt(disc, cart.full)) >= t.amount;
      } else if (t.type === 'gift') {
        t.reached = cart.total >= t.amount;
      } else {
        t.reached = axis >= t.pos;
      }
    });
    return { tiers: tiers, disc: disc, axis: axis, qty: qty };
  }

  // The next step, in words: "Spend another £15.26 to save 30%", "Add any 1
  // more bottle to save 20%", "Spend another £8.02 to get free delivery".
  function nextLine(t, lad, cart, cfg) {
    if (t.type === 'discount') {
      if (lad.qty) {
        var more = t.pos - lad.axis;
        return { text: fmtStr(more === 1 ? 'next_save_qty_one' : 'next_save_qty_other', { count: more, percent: t.pct }) };
      }
      var m = money(t.pos - lad.axis, cfg);
      return { text: fmtStr('next_save_money', { amount: m, percent: t.pct }), amount: m };
    }
    if (t.anyItem) return { text: cfg.giftAnyItem.replace('{reward}', t.reward || str('your_reward')) };
    var gap;
    if (t.type === 'delivery') {
      var at = cfg.deliveryBasis === 'before' ? t.amount : nextReach(t.amount, cart.full, lad.disc);
      gap = (at == null ? t.pos : at) - cart.full;
    } else {
      gap = t.amount - cart.total;
    }
    var amt = money(Math.max(gap, 1), cfg);
    return { text: cfg.spendMore.replace('{amount}', amt).replace('{reward}', t.reward || str('your_reward')), amount: amt };
  }

  function computeLadder(lad, cart, cfg, claimed) {
    var tiers = lad.tiers, n = tiers.length;
    var code = cfg.discountCode || '';
    var empty = lad.qty ? cart.count <= 0 : cart.full <= 0;

    // Fill: whole pills up to the last step the basket has passed, plus a
    // partial pill towards the next one, so each step's marker sits at the
    // right-hand edge of its pill.
    var passed = 0;
    while (passed < n && lad.axis >= tiers[passed].pos) passed++;
    var fill;
    if (passed >= n) fill = 1;
    else {
      var prev = passed === 0 ? 0 : tiers[passed - 1].pos;
      var span = tiers[passed].pos - prev;
      var seg = span > 0 ? Math.max(0, Math.min(1, (lad.axis - prev) / span)) : 0;
      fill = (passed + seg) / n;
    }

    var best = null, other = null, next = null, giftsReached = 0;
    tiers.forEach(function (t) {
      if (t.reached) {
        if (t.type === 'discount') { if (!best || t.pct > best.pct) best = t; }
        else if (t.type !== 'start' && t.success) other = t;
        if (t.type === 'gift' && t.kite) giftsReached++;
      } else if (!next && t.type !== 'start') {
        next = t;
      }
    });

    var headline = '', subtext = '', amount = null, celebrating = false, choosing = false;
    var maxPct = lad.disc.length ? lad.disc[lad.disc.length - 1].pct : 0;
    lad.disc.forEach(function (d) { if (d.pct > maxPct) maxPct = d.pct; });

    if (empty) {
      // Empty basket: say what the offer is, no instruction (27 Oct call).
      var first = lad.disc[0], top = lad.disc[lad.disc.length - 1];
      if (lad.qty) {
        headline = fmtStr('offer_qty', { count: first.pos, percent: first.pct });
        if (top !== first) subtext = fmtStr('offer_qty_sub', { count: top.pos, percent: top.pct });
      } else if (first) {
        headline = fmtStr('offer_money', { percent: maxPct });
        var del = null;
        tiers.forEach(function (t) { if (!del && t.type === 'delivery') del = t; });
        subtext = del
          ? fmtStr('offer_money_sub_delivery', { percent: first.pct, amount: moneyShort(first.pos, cfg), delivery: moneyShort(del.pos, cfg) })
          : fmtStr('offer_money_sub', { percent: first.pct, amount: moneyShort(first.pos, cfg) });
      } else {
        headline = next ? nextLine(next, lad, cart, cfg).text : cfg.doneHeadline;
      }
    } else {
      if (best) {
        celebrating = true;
        // The code rides in the headline while there is a next step to name
        // underneath; at the top step it gets the line below to itself.
        var inHead = !!code && !!next;
        if (lad.qty) {
          headline = fmtStr(inHead ? 'saved_qty_code' : 'saved_qty', { percent: best.pct, count: best.pos, code: code });
        } else {
          headline = fmtStr(inHead ? 'saved_money_code' : 'saved_money', { percent: best.pct, amount: money(savingAt(cart, best.pct), cfg), code: code });
        }
      } else if (other) {
        celebrating = true;
        headline = other.success;
      }
      if (next) {
        var nl = nextLine(next, lad, cart, cfg);
        amount = nl.amount || null;
        if (headline) subtext = nl.text;
        else headline = nl.text;
      } else if (headline) {
        subtext = code ? fmtStr('use_code', { code: code }) : cfg.doneSubtext;
      } else {
        headline = cfg.doneHeadline;
        subtext = cfg.doneSubtext;
      }
    }

    // Same rule as the plain bar: a gift reached but not yet in the basket.
    if (giftsReached > (claimed || 0) && cfg.giftUnlocked) {
      choosing = true;
      celebrating = true;
      headline = cfg.giftUnlocked;
      subtext = cfg.giftChoose || '';
      amount = null;
    }

    return { tiers: tiers, fillFraction: fill, headline: headline, subtext: subtext, amount: amount, celebrating: celebrating, choosing: choosing, empty: empty };
  }

  function ladderPill(t, lad, cfg) {
    if (t.type === 'start') return [fmtStr(t.pos === 1 ? 'bottles_one' : 'bottles_other', { count: t.pos }), ''];
    if (t.type === 'discount') {
      if (lad.qty) return [fmtStr('ladder_any', { count: t.pos }), fmtStr('ladder_save_lower', { percent: t.pct })];
      return [fmtStr('ladder_save', { percent: t.pct }), moneyShort(t.pos, cfg)];
    }
    if (t.type === 'delivery') return [str('free_delivery'), moneyShort(t.pos, cfg)];
    var label = t.kite
      ? (t.giftCount > 1 ? str('free_gift_numbered').replace('{number}', t.giftNumber) : str('free_gift'))
      : (t.label || str('reward'));
    return [label, t.anyItem ? '' : moneyShort(t.pos, cfg)];
  }

  function renderLadder(el, cfg, cart, kiteTiers, claimed) {
    var lad = buildLadder(cfg, kiteTiers, cart);
    var n = lad.tiers.length;
    if (!n) { el.setAttribute('data-threshold-empty', ''); el.innerHTML = ''; return; }
    el.removeAttribute('data-threshold-empty');
    var state = computeLadder(lad, cart, cfg, claimed);

    if (cfg.layout === 'steps') { renderSteps(el, ladderView(lad, state, cfg)); return; }

    if (n === 1 && el.hasAttribute('data-threshold-single-compact')) {
      var only = state.tiers[0];
      if (only.reached && only.type === 'discount') only.success = state.headline;
      renderSingle(el, state, !!only.reached);
      return;
    }

    // A reached step gets a tick. It goes on the shorter second line ("£50",
    // "save 20%") so a long first line like "Free delivery" still fits a
    // quarter of a phone screen; a one-line pill ("1 bottle") takes it there.
    var check = '<span class="noted-threshold-bar__check">' + iconSvg('check-circle') + '</span>';
    var pills = state.tiers.map(function (t) {
      var lines = ladderPill(t, lad, cfg);
      var tick = t.reached ? check : '';
      return '<div class="noted-threshold-bar__pill noted-threshold-bar__pill--ladder' + (t.reached ? ' is-complete' : '') + '">' +
        '<span class="noted-threshold-bar__label">' + (lines[1] ? '' : tick) + esc(lines[0]) + '</span>' +
        (lines[1] ? '<span class="noted-threshold-bar__sub">' + tick + esc(lines[1]) + '</span>' : '') +
        '</div>';
    }).join('');

    // A marker at the right-hand edge of each pill but the last: the point on
    // the bar where that step is reached (Kirill, 29 Oct).
    var markers = '';
    for (var i = 1; i < n; i++) {
      markers += '<span class="noted-threshold-bar__marker' + (state.tiers[i - 1].reached ? ' is-reached' : '') +
        '" style="left:' + (i / n * 100).toFixed(4) + '%"></span>';
    }

    var subHtml = '';
    if (state.subtext) {
      subHtml = esc(state.subtext);
      if (state.amount) {
        var amt = esc(state.amount);
        subHtml = subHtml.split(amt).join('<strong>' + amt + '</strong>');
      }
    }
    var tick = state.celebrating
      ? '<span class="noted-threshold-bar__tick">' + iconSvg('check-circle') + '</span>'
      : '';

    el.innerHTML =
      '<div class="noted-threshold-bar__track noted-threshold-bar__track--ladder">' +
        '<div class="noted-threshold-bar__pills" style="--pill-count:' + n + '">' + pills + '</div>' +
        '<div class="noted-threshold-bar__progress"><div class="noted-threshold-bar__fill" style="width:' + (state.fillFraction * 100).toFixed(2) + '%"></div>' + markers + '</div>' +
      '</div>' +
      '<p class="noted-threshold-bar__message">' + tick + '<strong>' + esc(state.headline) + '</strong>' +
        (subHtml ? '<br>' + subHtml : '') + '</p>';
    el.setAttribute('data-threshold-ready', '');
    el.setAttribute('data-threshold-ladder', lad.qty ? 'quantity' : 'money');
  }

  /* ───────────────────────── Steps layout ─────────────────── */
  // "Bar layout: Steps" (UK-1 / EU-1 mock-ups, Tim 3 Nov): the message
  // centred on top, the bar split into one segment per step, each step's
  // label and amount under its segment. Both tier models (plain delivery /
  // gift, and the spend and save ladder) are turned into one view:
  //   { segs: [{ label, sub, done, fill 0..1 }], msg: { tick, strong, text, amount } }
  // and the DOM is updated in place, so the fill, the label colour and the
  // message can animate between baskets instead of being redrawn.

  function clamp01(x) { return Math.max(0, Math.min(1, x)); }
  function segFill(value, prev, pos) {
    return pos > prev ? clamp01((value - prev) / (pos - prev)) : (value >= pos ? 1 : 0);
  }

  function plainView(state, valueOf, cfg) {
    var tiers = state.tiers;
    var segs = tiers.map(function (t, i) {
      var prev = i ? tiers[i - 1].amount : 0;
      var v = valueOf(t);
      return { label: labelFor(t), sub: t.anyItem ? '' : moneyShort(t.amount, cfg), done: v >= t.amount, fill: segFill(v, prev, t.amount) };
    });
    var msg = state.celebrating
      ? { tick: true, strong: state.headline, text: state.subtext }
      : { tick: false, strong: null, text: state.subtext || state.headline };
    msg.amount = state.amount;
    return { segs: segs, msg: msg };
  }

  function ladderView(lad, state, cfg) {
    var tiers = state.tiers;
    var segs = tiers.map(function (t, i) {
      var prev = i ? tiers[i - 1].pos : 0;
      var lines = ladderPill(t, lad, cfg);
      return { label: lines[0], sub: lines[1], done: !!t.reached, fill: segFill(lad.axis, prev, t.pos) };
    });
    var msg;
    if (state.celebrating) msg = { tick: true, strong: state.headline, text: state.subtext };
    else if (state.empty) msg = { tick: false, strong: state.headline, text: state.subtext };
    else msg = { tick: false, strong: null, text: state.headline };
    msg.amount = state.amount;
    return { segs: segs, msg: msg };
  }

  function stepsMsgHtml(m) {
    var html = '';
    if (m.tick || m.strong) {
      html += '<span class="noted-threshold-bar__steps-part">' +
        (m.tick ? '<span class="noted-threshold-bar__tick">' + iconSvg('check-circle') + '</span>' : '') +
        (m.strong ? '<strong>' + esc(m.strong) + '</strong>' : '') + '</span>';
    }
    if (m.text) {
      var t = esc(m.text);
      if (m.amount) { var a = esc(m.amount); t = t.split(a).join('<strong>' + a + '</strong>'); }
      html += '<span class="noted-threshold-bar__steps-part">' + t + '</span>';
    }
    return html;
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Where a bar lives. The drawer replaces its bar node on every basket
  // change; remembering the last state per place lets the new node start
  // where the old one was and animate from there, instead of snapping.
  var stepsMemory = {};
  function zoneKey(el) {
    var zone = el.closest('[data-cart-drawer]') ? 'drawer' : (el.closest('.quick-buy-sheet') ? 'quick' : 'page');
    var same = bars().filter(function (b) {
      return (b.closest('[data-cart-drawer]') ? 'drawer' : (b.closest('.quick-buy-sheet') ? 'quick' : 'page')) === zone;
    });
    return zone + ':' + Math.max(0, same.indexOf(el));
  }

  function renderSteps(el, view) {
    var sig = view.segs.map(function (s) { return s.label + '|' + s.sub; }).join('~');
    var key = zoneKey(el);
    var st = el.__steps;
    var animate = !reducedMotion();

    if (!st || st.sig !== sig || st.root.parentNode !== el) {
      // Build once per set of steps, at the last state this place showed
      // (or straight at the new one), with transitions held off.
      var mem = stepsMemory[key];
      var start = (mem && mem.sig === sig) ? mem.view : view;
      var n = view.segs.length;
      var segs = '', labels = '';
      start.segs.forEach(function (s) {
        segs += '<span class="noted-threshold-bar__steps-seg"><span class="noted-threshold-bar__steps-fill" style="width:' + (s.fill * 100).toFixed(2) + '%"></span></span>';
        labels += '<span class="noted-threshold-bar__steps-label' + (s.done ? ' is-done' : '') + '"><b>' + esc(s.label) + '</b>' +
          (s.sub ? '<span>' + esc(s.sub) + '</span>' : '') + '</span>';
      });
      var html = stepsMsgHtml(start.msg);
      el.innerHTML =
        '<div class="noted-threshold-bar__steps is-instant" style="--step-count:' + n + '">' +
          '<div class="noted-threshold-bar__steps-msg" aria-live="polite"><div class="noted-threshold-bar__steps-msg-inner">' + html + '</div></div>' +
          '<div class="noted-threshold-bar__steps-track" aria-hidden="true">' + segs + '</div>' +
          '<div class="noted-threshold-bar__steps-labels">' + labels + '</div>' +
        '</div>';
      el.setAttribute('data-threshold-ready', '');
      el.removeAttribute('data-threshold-ladder');
      var root = el.firstChild;
      st = el.__steps = {
        sig: sig, view: start, html: html, root: root,
        msg: root.querySelector('.noted-threshold-bar__steps-msg'),
        fills: root.querySelectorAll('.noted-threshold-bar__steps-fill'),
        labels: root.querySelectorAll('.noted-threshold-bar__steps-label')
      };
      void root.offsetWidth;            // commit the start state before transitions come on
      root.classList.remove('is-instant');
    }

    applySteps(st, view, animate);
    stepsMemory[key] = { sig: sig, view: view };
  }

  function applySteps(st, view, animate) {
    view.segs.forEach(function (s, i) {
      var before = st.view.segs[i];
      st.fills[i].style.width = (s.fill * 100).toFixed(2) + '%';
      var lab = st.labels[i];
      lab.classList.toggle('is-done', s.done);
      if (animate && s.done && before && !before.done) {
        lab.classList.remove('is-pop');
        void lab.offsetWidth;
        lab.classList.add('is-pop');
      }
    });
    var html = stepsMsgHtml(view.msg);
    if (html !== st.html) {
      if (animate) crossFade(st.msg, html);
      else st.msg.querySelector('.noted-threshold-bar__steps-msg-inner').innerHTML = html;
      st.html = html;
    }
    st.view = view;
  }

  // The old message fades out on top while the new one fades in, and the
  // box eases to the new height if the line count changes.
  function crossFade(box, html) {
    var inner = box.querySelector('.noted-threshold-bar__steps-msg-inner:not(.is-ghost)');
    Array.prototype.forEach.call(box.querySelectorAll('.is-ghost'), function (g) { g.parentNode.removeChild(g); });
    var h0 = box.offsetHeight;
    var ghost = inner.cloneNode(true);
    ghost.classList.add('is-ghost');
    box.appendChild(ghost);
    inner.innerHTML = html;
    inner.classList.add('is-entering');
    var h1 = inner.offsetHeight;
    if (h1 !== h0) { box.style.height = h0 + 'px'; void box.offsetHeight; box.style.height = h1 + 'px'; }
    requestAnimationFrame(function () {
      ghost.classList.add('is-out');
      inner.classList.remove('is-entering');
    });
    clearTimeout(box.__fadeT);
    box.__fadeT = setTimeout(function () {
      if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
      box.style.height = '';
    }, 420);
  }

  function labelFor(t) {
    if (t.kind === 'gift') return t.giftCount > 1 ? str('free_gift_numbered').replace('{number}', t.giftNumber) : str('free_gift');
    return t.label || (t.icon === 'truck' ? str('free_delivery') : (t.reward || str('reward')));
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function readConfig(el) {
    // The config <script> is rendered as the bar's first child by the snippet.
    var tag = el.querySelector('script[data-threshold-config]');
    if (!tag) return null;
    var cfg;
    try { cfg = JSON.parse(tag.textContent); } catch (e) { return null; }
    // Blank merchant settings fall back to the theme's language file
    // (locales/*.json → cart.threshold, surfaced as window.notedStrings).
    var fallback = {
      startHeadline: 'start_headline', doneHeadline: 'done_headline', doneSubtext: 'done_subtext',
      spendMore: 'spend_more', giftReward: 'gift_reward', giftSuccess: 'gift_success',
      giftSuccessMore: 'gift_success_more', giftUnlocked: 'gift_unlocked', giftChoose: 'gift_choose',
      giftAnyItem: 'gift_any_item'
    };
    for (var k in fallback) { if (!cfg[k]) cfg[k] = str(fallback[k]); }
    return cfg;
  }

  // Shopper-facing wording lives in the theme's language file, so a
  // translation app can reach it (snippets/js-strings.liquid).
  function str(key) {
    var s = window.notedStrings && window.notedStrings.threshold;
    return (s && s[key]) || '';
  }

  /* ───────────────────────── orchestration ────────────────── */
  var cartTotalCents = null;
  var giftsClaimed = 0;   // cart lines Kite has marked _free_product = 'true'

  function readClaimed(el) {
    var v = parseInt(el && el.getAttribute('data-gifts-claimed'), 10);
    if (isFinite(v)) giftsClaimed = v;
  }

  function bars() { return Array.prototype.slice.call(document.querySelectorAll('[data-threshold-bar]')); }

  // The spend and save side of the basket: full-price total, bottle count and
  // full-price line prices, all leaving Kite gift lines out. Rendered by the
  // snippet for first paint, refreshed from /cart.js on every change.
  var basket = { full: null, count: 0, lines: [] };

  function readBasket(el) {
    var f = parseInt(el.getAttribute('data-cart-full-total'), 10);
    var n = parseInt(el.getAttribute('data-cart-count'), 10);
    var raw = el.getAttribute('data-cart-lines');
    if (isFinite(f)) basket.full = f;
    if (isFinite(n)) basket.count = n;
    if (raw != null) {
      basket.lines = raw ? raw.split(',').map(function (x) { return parseInt(x, 10) || 0; }) : [];
    }
  }

  function cartState() {
    return {
      total: cartTotalCents || 0,
      full: basket.full == null ? (cartTotalCents || 0) : basket.full,
      count: basket.count,
      lines: basket.lines
    };
  }

  function seedTotal() {
    if (cartTotalCents != null) return;
    var el = document.querySelector('[data-threshold-bar][data-cart-total]');
    if (el) { var v = parseInt(el.getAttribute('data-cart-total'), 10); if (isFinite(v)) cartTotalCents = v; readClaimed(el); readBasket(el); }
    if (cartTotalCents == null) cartTotalCents = 0;
  }

  function renderAll(kiteTiers) {
    seedTotal();
    var cart = cartState();
    bars().forEach(function (el) { render(el, cart, kiteTiers, giftsClaimed); });
  }

  function isGiftLine(it) {
    return it && it.properties && String(it.properties._free_product) === 'true';
  }

  function refreshTotal() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (c && typeof c.total_price === 'number') cartTotalCents = c.total_price;
        if (c && Array.isArray(c.items)) {
          giftsClaimed = c.items.filter(isGiftLine).length;
          var paid = c.items.filter(function (it) { return it && !isGiftLine(it); });
          basket.lines = paid.map(function (it) { return it.original_line_price || 0; });
          basket.full = basket.lines.reduce(function (s, l) { return s + l; }, 0);
          basket.count = paid.reduce(function (s, it) { return s + (it.quantity || 0); }, 0);
        }
        return cartTotalCents;
      })
      .catch(function () { return cartTotalCents; });
  }

  // Pull live Kite tiers if ready (and refresh the cache); otherwise fall back to
  // the cached set so the bar is complete on first paint with no flash.
  function currentKite() {
    var live = readKiteTiers();
    if (live) { cacheKite(live); return live; }
    return cachedKite();
  }

  function update() { renderAll(currentKite()); }

  function start() {
    seedTotal();
    // First paint: custom tiers (server config) + cached Kite tiers — synchronous,
    // no network, no flash.
    renderAll(cachedKite());

    // Reconcile Kite as soon as window.gfg is ready (poll briefly after load).
    var tries = 0;
    (function pollKite() {
      var live = readKiteTiers();
      if (live) { cacheKite(live); renderAll(live); return; }
      if (tries++ < 20) setTimeout(pollKite, 300);
    })();

    // Cart changes: refresh the total, then re-render (PDP needs this; the drawer
    // also re-renders its server HTML, which we re-hydrate here too).
    document.addEventListener('cart:change', function () {
      refreshTotal().then(update);
    });

    // The drawer swaps [data-cart-rerender] innerHTML, replacing the bar node with
    // fresh server markup (new config + the current cart total in data-cart-total).
    // Trust that server-fresh total immediately so there's no stale flash before
    // the cart:change fetch lands, then re-hydrate the Kite tiers on top.
    var raf = null;
    function newBar(node) {
      if (node.nodeType !== 1) return null;
      if (node.matches && node.matches('[data-threshold-bar]')) return node;
      return node.querySelector ? node.querySelector('[data-threshold-bar]') : null;
    }
    var obs = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        for (var j = 0; j < muts[i].addedNodes.length; j++) {
          var bar = newBar(muts[i].addedNodes[j]);
          if (bar) {
            var v = parseInt(bar.getAttribute('data-cart-total'), 10);
            if (isFinite(v)) cartTotalCents = v;
            readClaimed(bar);
            readBasket(bar);
            // Steps: draw the fresh node now, inside this callback, so no frame
            // can paint it empty; it starts from the last state and animates.
            if (bar.getAttribute('data-threshold-layout') === 'steps') { update(); return; }
            if (raf) return; raf = requestAnimationFrame(function () { raf = null; update(); });
            return;
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
